import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useSite, waLink } from "../siteContext";
import { SectionBackdrop } from "./SectionBackdrop";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const settings = useSite();
  const { lang, s } = useLang();
  const INTERESTS = s.interests;
  const [form, setForm] = useState({ name: "", phone: "", interest: s.interests[2], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error(s.form_required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(s.form_success);
      setForm({ name: "", phone: "", interest: s.interests[2], message: "" });
    } catch {
      toast.error(s.form_error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-36 bg-luxury-dark border-t border-luxury-border">
      <SectionBackdrop id="contact" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{s.contact_eyebrow}</p>
          <h2 data-testid="contact-title" className="font-serif font-bold text-4xl sm:text-5xl text-luxury-bone leading-[1.25] mb-8">
            {s.contact_title_a} <span className="gold-gradient-text italic">{s.contact_title_b}</span>
          </h2>
          <p className="text-base md:text-lg text-luxury-gray leading-loose mb-10">
            {s.contact_text}
          </p>
          <a
            data-testid="contact-whatsapp-button"
            href={waLink(settings, undefined, lang)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 border border-luxury-gold text-luxury-gold px-8 py-4 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300 font-semibold"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
            {s.contact_whatsapp}
          </a>
          <a
            data-testid="contact-email-link"
            href={`mailto:${settings.email}`}
            className="mt-6 flex items-center gap-3 text-luxury-gray hover:text-luxury-gold transition-colors"
          >
            <Mail className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
            <span dir="ltr">{settings.email}</span>
          </a>
          {settings.support_email ? (
            <a
              data-testid="contact-support-email-link"
              href={`mailto:${settings.support_email}`}
              className="mt-3 flex items-center gap-3 text-luxury-gray hover:text-luxury-gold transition-colors"
            >
              <Mail className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
              <span dir="ltr">{settings.support_email}</span>
              <span className="text-xs">({lang === "ar" ? "للدعم" : "Support"})</span>
            </a>
          ) : null}
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 bg-luxury-surface border border-luxury-border rounded-2xl p-8 md:p-12 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-luxury-gray mb-2" htmlFor="contact-name">{s.form_name}</label>
              <Input
                id="contact-name"
                data-testid="contact-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder={s.form_name_ph}
                className="bg-luxury-black border-luxury-border text-luxury-bone placeholder:text-luxury-gray/50 focus-visible:ring-luxury-gold h-12"
              />
            </div>
            <div>
              <label className="block text-sm text-luxury-gray mb-2" htmlFor="contact-phone">{s.form_phone}</label>
              <Input
                id="contact-phone"
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="05xxxxxxxx"
                dir="ltr"
                className="bg-luxury-black border-luxury-border text-luxury-bone placeholder:text-luxury-gray/50 focus-visible:ring-luxury-gold h-12 text-right"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-luxury-gray mb-2">{s.form_interest}</label>
            <div className="flex flex-wrap gap-3" data-testid="contact-interest-group">
              {INTERESTS.map((opt, oi) => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`contact-interest-${oi}`}
                  onClick={() => setForm({ ...form, interest: opt })}
                  className={`px-5 py-2.5 rounded-full text-sm border transition-colors duration-300 ${
                    form.interest === opt
                      ? "bg-luxury-gold text-luxury-black border-luxury-gold font-semibold"
                      : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-luxury-gray mb-2" htmlFor="contact-message">{s.form_message}</label>
            <Textarea
              id="contact-message"
              data-testid="contact-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder={s.form_message_ph}
              rows={5}
              className="bg-luxury-black border-luxury-border text-luxury-bone placeholder:text-luxury-gray/50 focus-visible:ring-luxury-gold resize-none"
            />
          </div>

          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-3 bg-luxury-gold text-luxury-black font-semibold px-8 py-4 rounded-full hover:bg-luxury-goldLight transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? "…" : null}
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={1.5} />}
            {sending ? s.form_sending : s.form_submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
